/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { DisabledAccessPotential, TriState } from "../models";
import { TourismPotential } from "../models";
import { CaveRating } from "../models";
import { ExplorationPotential } from "../models";
import { LocationType } from "../models";
import { IMedia, IPlace } from "./models/Models";
import { MediaType, getMediaTypeFromMimeType } from "./Media";
import { Kernel } from "./Kernel";
import cloneDeep from "lodash-es/cloneDeep.js";
import isEqual from "lodash-es/isEqual.js";
import { GroundWaterTable } from "./backend/cloud/GroundWaterTable";
import { ProtectionLevel, groupAccessToProtectionLevel } from "./User";
import { EntryType, WaterType } from "../API";
import { IPlaceTable } from "./backend/IPlaceTable";

export const AllLocationTypes = [
    LocationType.UNKNOWN,
    LocationType.NOT_FOUND,
    LocationType.DEPRESSION,
    LocationType.CAVE,
    LocationType.SINKHOLE,
    LocationType.SOLUTION_TUBE,
    LocationType.DESTROYED,
    LocationType.ROCKY_OUTCROP,
    LocationType.LANDMARK,
    LocationType.MINE,
    LocationType.WRECK,
    LocationType.REEF,
    LocationType.JETTY,
    LocationType.MUCK,
    LocationType.SAND,
    LocationType.OCEAN_WALL,
    LocationType.CAMPSITE
];

export const DefaultLocationTypes = [
    LocationType.CAVE,
    LocationType.SINKHOLE,
    LocationType.MINE,
    LocationType.WRECK,
    LocationType.REEF,
    LocationType.JETTY,
    LocationType.MUCK,
    LocationType.SAND,
    LocationType.OCEAN_WALL,
    LocationType.LANDMARK,
    LocationType.CAMPSITE
];

export const AllExplorationPotentials = [
    ExplorationPotential.UNKNOWN,
    ExplorationPotential.NONE,
    ExplorationPotential.LOW,
    ExplorationPotential.MEDIUM,
    ExplorationPotential.HIGH,
    ExplorationPotential.VERY_HIGH,
];

export const AllDisabledAccessPotentials = [
    DisabledAccessPotential.UNKNOWN,
    DisabledAccessPotential.NONE,
    DisabledAccessPotential.LOW,
    DisabledAccessPotential.MEDIUM,
    DisabledAccessPotential.HIGH,
];

export const AllTourismPotentials = [
    TourismPotential.UNKNOWN,
    TourismPotential.NONE,
    TourismPotential.LOW,
    TourismPotential.MEDIUM,
    TourismPotential.HIGH,
];

export const AllCaveRatings = [
    CaveRating.UNKNOWN,
    CaveRating.NONE,
    CaveRating.BASIC,
    CaveRating.CAVE,
    CaveRating.ADVANCED,
];

export const AllTriState = [TriState.UNKNOWN, TriState.NO, TriState.YES];

export enum BooleanEnum {
    True = "True",
    False = "False",
}

export interface IFilter {
    /// Filter by list of place ids and ignore all other filters
    list?: string[];
    deleted: boolean;
    type?: Array<LocationType>;
    exploration?: Array<ExplorationPotential>;
    disabledAccess?: Array<DisabledAccessPotential>;
    tourism?: Array<TourismPotential>;
    caveRating?: Array<CaveRating>;
    verticalAccess?: Array<TriState>;
    goesToWater?: Array<TriState>;
    nameQuery?: string;
    nameOrIdQuery?: string;
    notesQuery?: string;
    referenceQuery?: string;
    sourceQuery?: string;
    labelsQuery?: string;
    checkedByQuery?: string;
    idQuery?: string;
    hasSurvey?: BooleanEnum;
    hasReports?: BooleanEnum;
    hasLocation?: BooleanEnum;
    checked?: BooleanEnum;
    withinHeightOfWaterTable: number | undefined;
    country?: string;
    state?: string;
    hasDesc?: BooleanEnum;
    protectionLevel?: Array<ProtectionLevel>;
    entryType?: EntryType;
    waterType?: WaterType;
}

export function compareFilters(filter1: IFilter, filter2: IFilter) {
    return isEqual(filter1, filter2);
}

export function cloneFilter(filter: IFilter) {
    return cloneDeep(filter);
}

export function filterIsDefault(filter: IFilter) {
    if (!filter) {
        return false;
    }
    return compareFilters(filter, makeDefaultFilter());
}

function queryStringMatch(query: string | undefined, str: string | string[] | undefined | null) {
    if (!query || query.length === 0) return true;

    query = query.toLowerCase();
    if (Array.isArray(str)) {
        for (const s of str) {
            if (s.toLowerCase().includes(query)) {
                return true;
            }
        }
        return false;
    } else {
        return str && str.toLowerCase().includes(query) ? true : false;
    }
}

// Returns true if all entries of array2 are in array1
function arrayIncludes<T>(array1: Array<T>, array2: Array<T>) {
    if (array2.length === 0) return true;
    if (array2.length > array1.length) return false;
    for (const elem of array2) {
        if (!array1.includes(elem)) return false;
    }
    return true;
}

function testBooleanOrAll(value: boolean, boa: BooleanEnum | undefined) {
    if (boa === undefined) {
        return true;
    }

    if (boa === BooleanEnum.True) {
        return value;
    }

    return !value;
}

function testHeightFromWaterTable(delta: number, place: IPlace): boolean {
    const groundWaterTable = Kernel.instance().dataStore.getSource(
        "ground-water"
    ) as GroundWaterTable;
    const dist = groundWaterTable.distanceFromLocation(place.data);
    if (dist) {
        if (dist < 0) {
            // underwater
            return true;
        }
        return dist < delta;
    }

    return false;
}

type FilterFuncType = (filter: IFilter, place: IPlace, context?: any) => boolean;

export type CompiledFilter = {
    filter: IFilter;
    checks: FilterFuncType[];
};

export function compileFilters(filter: IFilter): CompiledFilter {
    const checks: FilterFuncType[] = [];

    const addCheck = (condition: any, func: (f: IFilter, p: IPlace, context: any) => boolean) => {
        if (condition) {
            checks.push(func);
        }
    };

    checks.push((_f: IFilter, p: IPlace) => !p.softDeleted || p.softDeleted === "false");

    // If the filter has a list of place ids, ignore all other filters
    if (filter.list && filter.list.length > 0) {
        checks.push((f: IFilter, p: IPlace) => {
            return f.list!.includes(p.id);
        });
    }
    // Querying by nameOrId disregards all other filters
    else if (filter.nameOrIdQuery && filter.nameOrIdQuery.length > 0) {
        checks.push((f: IFilter, p: IPlace) => {
            return (
                queryStringMatch(f.nameOrIdQuery, p.data.name) ||
                queryStringMatch(f.nameOrIdQuery, p.data.alternateNames) ||
                queryStringMatch(f.nameOrIdQuery, p.id) ||
                queryStringMatch(f.nameQuery, p.data.spelioData.systemName)
            );
        });
    } else {
        addCheck(filter.type, (f: IFilter, p: IPlace) =>
            f.type!.includes(p.data.spelioData.type as LocationType)
        );
        addCheck(filter.exploration, (f: IFilter, p: IPlace) =>
            f.exploration!.includes(p.data.spelioData.explorationPotential as ExplorationPotential)
        );

        addCheck(filter.disabledAccess, (f: IFilter, p: IPlace) =>
            f.disabledAccess!.includes(
                p.data.spelioData.disabledAccessPotential as DisabledAccessPotential
            )
        );

        addCheck(filter.tourism, (f: IFilter, p: IPlace) =>
            f.tourism!.includes(p.data.spelioData.tourismPotential as TourismPotential)
        );

        addCheck(filter.verticalAccess, (f: IFilter, p: IPlace) =>
            f.verticalAccess!.includes(p.data.spelioData.verticalAccess as TriState)
        );
        addCheck(filter.goesToWater, (f: IFilter, p: IPlace) =>
            f.goesToWater!.includes(p.data.spelioData.goesToWater as TriState)
        );

        addCheck(filter.nameQuery, (f: IFilter, p: IPlace) => {
            return (
                queryStringMatch(f.nameQuery, p.data.name) ||
                queryStringMatch(f.nameQuery, p.data.alternateNames) ||
                queryStringMatch(f.nameQuery, p.data.spelioData.systemName)
            );
        });
        addCheck(filter.notesQuery, (f: IFilter, p: IPlace) =>
            queryStringMatch(f.notesQuery, p.data.spelioData.notes)
        );
        addCheck(filter.referenceQuery, (f: IFilter, p: IPlace) =>
            queryStringMatch(f.referenceQuery, p.data.spelioData.reference)
        );
        addCheck(filter.sourceQuery, (f: IFilter, p: IPlace) =>
            queryStringMatch(f.sourceQuery, p.data.spelioData.source)
        );
        addCheck(filter.checkedByQuery, (f: IFilter, p: IPlace) =>
            queryStringMatch(f.checkedByQuery, p.data.checkedBy)
        );
        addCheck(filter.labelsQuery, (f: IFilter, p: IPlace) =>
            arrayIncludes(
                p.data.spelioData.labels.map((x) => x.trim()),
                f
                    .labelsQuery!.split(",")
                    .map((x) => x.trim())
                    .filter((x) => x && x.length)
            )
        );

        addCheck(filter.hasSurvey, (f: IFilter, _p: IPlace, context: any) =>
            testBooleanOrAll(
                context && context.media
                    ? context.media.filter((x: IMedia) =>
                          [MediaType.Kml, MediaType.GeoJson].includes(
                              getMediaTypeFromMimeType(x?.mimeType)
                          )
                      ).length > 0
                    : false,
                f.hasSurvey!
            )
        );

        addCheck(filter.hasReports, (f: IFilter, _p: IPlace, context: any) =>
            testBooleanOrAll(
                context && context.reports && context.reports.length > 0,
                f.hasReports!
            )
        );

        addCheck(filter.hasLocation, (f: IFilter, p: IPlace) =>
            testBooleanOrAll(p.data.locationKnown, f.hasLocation)
        );
        addCheck(filter.checked, (f: IFilter, p: IPlace) =>
            testBooleanOrAll(p.data.checked, f.checked)
        );
        addCheck(filter.idQuery, (f: IFilter, p: IPlace) => queryStringMatch(f.idQuery, p.id));
        addCheck(filter.withinHeightOfWaterTable, (f: IFilter, p: IPlace) =>
            testHeightFromWaterTable(f.withinHeightOfWaterTable!, p)
        );

        addCheck(filter.caveRating, (f: IFilter, p: IPlace) =>
            f.caveRating!.includes(p.data.spelioData.caveRating as CaveRating)
        );
        addCheck(filter.country, (f: IFilter, p: IPlace) => p.data.address?.country === f.country);
        addCheck(filter.state, (f: IFilter, p: IPlace) => p.data.address?.state === f.state);
        addCheck(filter.hasDesc, (f: IFilter, p: IPlace) =>
            f.hasDesc === BooleanEnum.True
                ? p.data.spelioData.notes.length > 0
                : p.data.spelioData.notes.length === 0
        );

        addCheck(filter.protectionLevel, (f: IFilter, p: IPlace) => {
            const placeLevel = groupAccessToProtectionLevel(p.groupAccess);
            if (placeLevel) {
                return f.protectionLevel!.includes(placeLevel as ProtectionLevel);
            }
            return false;
        });

        addCheck(filter.entryType, (f: IFilter, p: IPlace) => {
            return f.entryType!.includes(p.data.diveData?.entryType as EntryType);
        });
    }

    return {
        filter,
        checks,
    };
}

export function compiledFilterPlace(filter: CompiledFilter, place: IPlace, context?: any) {
    for (const check of filter.checks) {
        if (!check(filter.filter, place, context)) return true;
    }

    return false;
}

export async function compiledFilterPlaceAsync(
    filter: CompiledFilter,
    place: IPlace
): Promise<boolean> {
    const placeTable = Kernel.instance().dataStore.getSource<IPlaceTable>("places");
    const context: any = {};

    if (filter.filter.hasSurvey) {
        context.media = await placeTable.getMediaItems(place.id);
    }

    if (filter.filter.hasReports) {
        context.reports = await placeTable.getReports(place.id);
    }

    for (const check of filter.checks) {
        if (!check(filter.filter, place, context)) {
            return true;
        }
    }

    return false;
}

export function filterPlace(filter: IFilter, place: IPlace, context?: any): boolean {
    const cf = compileFilters(filter);
    return compiledFilterPlace(cf, place, context);
}

export function makeDefaultFilter(): IFilter {
    return {
        list: undefined,
        deleted: false,
        type: [...DefaultLocationTypes],
        exploration: undefined,
        disabledAccess: undefined,
        tourism: undefined,
        caveRating: undefined,
        verticalAccess: undefined,
        nameQuery: "",
        notesQuery: "",
        referenceQuery: "",
        sourceQuery: "",
        labelsQuery: "",
        idQuery: "",
        goesToWater: undefined,
        checkedByQuery: "",
        hasSurvey: undefined,
        hasReports: undefined,
        hasLocation: BooleanEnum.True,
        checked: undefined,
        nameOrIdQuery: "",
        withinHeightOfWaterTable: undefined,
        hasDesc: undefined,
        protectionLevel: undefined,
        entryType: undefined,
        waterType: undefined,
    };
}
